import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useQuery, gql } from '@apollo/client';
import { Link } from 'react-router-dom';
import { Button } from '@artstorefronts/ui';
import { Loader2 } from 'lucide-react';

import AdminLayout from '../../../components/AdminLayout';

const PAGE_SIZE = 25;

export const ADMIN_UNMATCHED_CLAIMS_QUERY = gql`
  query AdminUnmatchedClaims($limit: Int, $offset: Int) {
    adminUnmatchedClaims(limit: $limit, offset: $offset) {
      items {
        id
        slug
        scrapedEmailExisted
        createdAt
        emailVerifiedAt
        claimedAt
        status
        ownerAsfId
        ownerSlug
        sameNetworkCount
      }
      totalCount
      hasMore
    }
  }
`;

type UnmatchedClaimStatus =
  | 'claimed'
  | 'account_creation_in_progress'
  | 'verified'
  | 'pending_verification'
  | 'expired';

interface UnmatchedClaim {
  id: string;
  slug: string;
  scrapedEmailExisted: boolean | null;
  createdAt: string;
  emailVerifiedAt: string | null;
  claimedAt: string | null;
  status: UnmatchedClaimStatus;
  ownerAsfId: string | null;
  ownerSlug: string | null;
  sameNetworkCount: number;
}

const GRID = 'grid grid-cols-[1fr_110px_110px_110px_110px_150px_120px_100px]';

function getStatusPillClasses(status: UnmatchedClaimStatus) {
  switch (status) {
    case 'claimed':
      return 'bg-green-100 text-green-800 border-green-200';
    case 'account_creation_in_progress':
    case 'verified':
      return 'bg-amber-100 text-amber-800 border-amber-200';
    case 'pending_verification':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
}

function scrapedEmailLabel(t: TFunction<'admin'>, value: boolean | null) {
  if (value === null) return t('unmatched-claims.scraped-email.unknown');
  return value ? t('unmatched-claims.scraped-email.yes') : t('unmatched-claims.scraped-email.no');
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

export default function UnmatchedClaimsPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const [loadingMore, setLoadingMore] = useState(false);
  // Advance a full page per fetch: a page that dedupes to nothing must still move the offset.
  const nextOffsetRef = useRef(PAGE_SIZE);

  const { data, loading, fetchMore } = useQuery(ADMIN_UNMATCHED_CLAIMS_QUERY, {
    variables: { limit: PAGE_SIZE, offset: 0 },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const claims = (data?.adminUnmatchedClaims?.items || []) as UnmatchedClaim[];
  const totalCount = (data?.adminUnmatchedClaims?.totalCount ?? 0) as number;
  const hasMore = Boolean(data?.adminUnmatchedClaims?.hasMore);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading || loadingMore) return;
    const offset = nextOffsetRef.current;
    setLoadingMore(true);
    try {
      await fetchMore({
        variables: { limit: PAGE_SIZE, offset },
        updateQuery: (prev, { fetchMoreResult }) => {
          const incoming = fetchMoreResult?.adminUnmatchedClaims;
          if (!incoming) return prev;
          const existingIds = new Set(prev.adminUnmatchedClaims.items.map((claim: UnmatchedClaim) => claim.id));
          const newItems = incoming.items.filter((claim: UnmatchedClaim) => !existingIds.has(claim.id));
          return {
            adminUnmatchedClaims: { ...incoming, items: [...prev.adminUnmatchedClaims.items, ...newItems] },
          };
        },
      });
      nextOffsetRef.current = offset + PAGE_SIZE;
    } catch (error) {
      console.error('Error loading more unmatched claims:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchMore, hasMore, loading, loadingMore]);

  if (loading && !data) {
    return (
      <AdminLayout title={t('unmatched-claims.page-title')} description={t('unmatched-claims.page-description')}>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title={t('unmatched-claims.page-title')} description={t('unmatched-claims.page-description')}>
      <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
        <div className="min-w-[960px]">
          <div className="bg-gray-50 border-b border-gray-200">
            <div className={`${GRID} py-3 px-4 text-xs font-medium text-gray-500 uppercase tracking-wide`}>
              <div>{t('unmatched-claims.table.profile')}</div>
              <div>{t('unmatched-claims.table.scraped-email')}</div>
              <div>{t('unmatched-claims.table.started')}</div>
              <div>{t('unmatched-claims.table.verified')}</div>
              <div>{t('unmatched-claims.table.claimed')}</div>
              <div>{t('unmatched-claims.table.status')}</div>
              <div>{t('unmatched-claims.table.owner')}</div>
              <div>{t('unmatched-claims.table.same-network')}</div>
            </div>
          </div>

          {claims.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>{t('unmatched-claims.empty-state')}</p>
            </div>
          ) : (
            claims.map((claim) => (
              <div
                key={claim.id}
                data-testid="unmatched-claim-row"
                className={`${GRID} items-center px-4 py-3 text-sm border-b border-gray-100`}
              >
                <div className="min-w-0">
                  <a
                    href={`${__TOWN_SQUARE_URL__}/${claim.slug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-medium text-gray-900 hover:underline truncate block"
                  >
                    {claim.slug}
                  </a>
                </div>
                <div className="text-gray-600 text-xs">{scrapedEmailLabel(t, claim.scrapedEmailExisted)}</div>
                <div className="text-gray-600 text-xs">{formatDate(claim.createdAt)}</div>
                <div className="text-gray-600 text-xs">{formatDate(claim.emailVerifiedAt)}</div>
                <div className="text-gray-600 text-xs">{formatDate(claim.claimedAt)}</div>
                <div>
                  <span
                    className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getStatusPillClasses(claim.status)}`}
                  >
                    {t(`unmatched-claims.status.${claim.status}`)}
                  </span>
                </div>
                <div className="text-xs">
                  {claim.ownerAsfId ? (
                    <Link to={`/admin/users/${claim.ownerAsfId}/edit`} className="text-gray-900 hover:underline">
                      {t('unmatched-claims.view-owner')}
                    </Link>
                  ) : (
                    <span className="text-gray-400">{t('unmatched-claims.no-owner')}</span>
                  )}
                </div>
                <div className={claim.sameNetworkCount > 0 ? 'text-amber-700 font-medium' : 'text-gray-600'}>
                  {claim.sameNetworkCount}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {claims.length > 0 && (
        <div className="mt-4 flex flex-col items-center gap-3">
          <div className="text-xs text-gray-500">
            {t('unmatched-claims.showing-count', { shown: claims.length, total: totalCount })}
          </div>
          {hasMore && (
            <Button
              variant="outline"
              onClick={loadMore}
              disabled={loadingMore}
              className="border-gray-200 text-gray-600 hover:bg-gray-50"
            >
              {loadingMore ? (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{tc('loading-more')}</span>
                </div>
              ) : (
                <span>{tc('load-more')}</span>
              )}
            </Button>
          )}
        </div>
      )}
    </AdminLayout>
  );
}
